import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from './Navbar';
import Footer from './Footer';
import { 
  FaShieldAlt, 
  FaWind, 
  FaUmbrella, 
  FaTools, 
  FaCheckCircle,
  FaArrowRight,
  FaCertificate,
  FaHardHat
} from 'react-icons/fa';
import { trackPageView } from '../utils/tracking';

function WhyChooseUs() {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView('/why-choose-us', 'Why Choose Us Page');
  }, []);

  return (
    <>
      <Helmet>
        <title>Storm-Proof & Rust-Proof Solar Structures | Lunar Electric</title>
        <meta name="description" content="Discover why Lunar Electric solar installations are built to last. Storm-proof and rust-proof structures engineered to withstand extreme weather. 25+ year durability guarantee." />
        <meta name="keywords" content="storm-proof solar, rust-proof solar, durable solar structures, weather-resistant solar panels, corrosion-proof solar installation, wind-resistant solar, long-lasting solar systems, Lunar Electric" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:title" content="Storm-Proof & Rust-Proof Solar Structures | Lunar Electric" />
        <meta property="og:description" content="Built to last: Storm-proof and rust-proof solar structures engineered to withstand extreme weather conditions. 25+ year durability guarantee." />
        <meta property="og:url" content="https://lunarelectric.in/why-choose-us" />
        <meta name="twitter:title" content="Storm-Proof & Rust-Proof Solar Structures | Lunar Electric" />
        <meta name="twitter:description" content="Built to last: Storm-proof and rust-proof solar structures engineered to withstand extreme weather conditions. 25+ year durability guarantee." />
        <link rel="canonical" href="https://lunarelectric.in/why-choose-us" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Storm-Proof & Rust-Proof Solar Installation",
          "description": "Professional solar panel installation with storm-proof and rust-proof mounting structures built to last 25+ years",
          "brand": {
            "@type": "Brand",
            "name": "Lunar Electric"
          },
          "offers": {
            "@type": "Offer",
            "priceCurrency": "INR",
            "availability": "https://schema.org/InStock"
          },
          "additionalProperty": [
            {
              "@type": "PropertyValue",
              "name": "Storm Resistance",
              "value": "Engineered to withstand extreme weather conditions and high winds"
            },
            {
              "@type": "PropertyValue",
              "name": "Rust Protection",
              "value": "Galvanized steel with premium coatings for zero corrosion"
            },
            {
              "@type": "PropertyValue",
              "name": "Durability",
              "value": "25+ years lifespan with minimal maintenance"
            }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://lunarelectric.in/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Why Choose Us",
              "item": "https://lunarelectric.in/why-choose-us"
            }
          ]
        })}</script>
      </Helmet>
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="why-choose-hero">
          <div className="why-choose-hero-container">
            <div className="why-choose-hero-image-wrapper">
              <img 
                src="./img/storm proof structure2.png" 
                alt="Storm-proof and rust-proof solar panel mounting structure" 
                className="why-choose-hero-image"
                loading="eager"
              />
            </div>
            <div className="why-choose-hero-content">
              <h1 className="why-choose-hero-title">
                Built to Last: Storm-Proof & Rust-Proof Solar Structures
              </h1>
              <p className="why-choose-hero-subtitle">
                Your solar investment deserves protection. Our structures are engineered to withstand extreme weather, resist corrosion, and deliver reliable performance for 25+ years.
              </p>
              <div className="why-choose-hero-features">
                <div className="hero-feature-item">
                  <FaShieldAlt className="hero-feature-icon" />
                  <span>Storm-Resistant Up to 150 km/h</span>
                </div>
                <div className="hero-feature-item">
                  <FaShieldAlt className="hero-feature-icon" />
                  <span>100% Rust-Proof Materials</span>
                </div>
                <div className="hero-feature-item">
                  <FaShieldAlt className="hero-feature-icon" />
                  <span>25+ Year Lifespan</span>
                </div>
              </div>
              <div className="why-choose-hero-cta">
                <Link to="/callback" className="btn btn-primary btn-large">
                  Get Your Storm-Proof & Rust-Proof Solar System
                  <FaArrowRight style={{ marginLeft: '8px' }} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Storm-Proof Engineering Section */}
        <section className="why-choose-feature-section storm-proof-section">
          <div className="container">
            <div className="why-choose-feature-wrapper">
              <div className="why-choose-feature-content">
                <div className="why-choose-feature-icon-large">
                  <FaWind size={48} style={{ color: '#1F3A93' }} />
                </div>
                <h2 className="why-choose-feature-title">Storm-Proof Engineering</h2>
                <p className="why-choose-feature-intro">
                  Our solar mounting structures are engineered to withstand extreme weather conditions, high winds, and severe storms. Your investment stays protected, no matter what nature throws at it.
                </p>
                
                <div className="why-choose-feature-details">
                  <div className="feature-detail-item">
                    <FaCheckCircle className="feature-check-icon" />
                    <div>
                      <h3>High Wind Resistance</h3>
                      <p>Structures designed to withstand wind speeds up to 150 km/h, meeting and exceeding international safety standards</p>
                    </div>
                  </div>
                  <div className="feature-detail-item">
                    <FaCheckCircle className="feature-check-icon" />
                    <div>
                      <h3>Structural Engineering</h3>
                      <p>Pre-fabricated mounting systems with reinforced connections and load-bearing calculations for maximum stability</p>
                    </div>
                  </div>
                  <div className="feature-detail-item">
                    <FaCheckCircle className="feature-check-icon" />
                    <div>
                      <h3>Weather Testing</h3>
                      <p>All components undergo rigorous testing for extreme weather conditions including heavy rain, hail, and temperature variations</p>
                    </div>
                  </div>
                  <div className="feature-detail-item">
                    <FaCheckCircle className="feature-check-icon" />
                    <div>
                      <h3>Certified Installation</h3>
                      <p>Professional installation by certified engineers following industry best practices and safety protocols</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Rust-Proof Materials Section */}
        <section className="why-choose-feature-section rust-proof-section">
          <div className="container">
            <div className="why-choose-feature-wrapper">
              <div className="why-choose-feature-content">
                <div className="why-choose-feature-icon-large">
                  <FaShieldAlt size={48} style={{ color: '#10B981' }} />
                </div>
                <h2 className="why-choose-feature-title">Rust-Proof Materials</h2>
                <p className="why-choose-feature-intro">
                  Galvanized steel and premium coatings ensure zero corrosion for decades. Your solar structure maintains its integrity and appearance year after year, even in harsh environments.
                </p>
                
                <div className="why-choose-feature-details">
                  <div className="feature-detail-item">
                    <FaCheckCircle className="feature-check-icon" />
                    <div>
                      <h3>Galvanized Steel</h3>
                      <p>Hot-dip galvanized steel frames provide superior corrosion resistance, protecting against rust even in coastal and high-humidity areas</p>
                    </div>
                  </div>
                  <div className="feature-detail-item">
                    <FaCheckCircle className="feature-check-icon" />
                    <div>
                      <h3>Premium Coatings</h3>
                      <p>Advanced protective coatings and powder coating finishes add an extra layer of protection against environmental factors</p>
                    </div>
                  </div>
                  <div className="feature-detail-item">
                    <FaCheckCircle className="feature-check-icon" />
                    <div>
                      <h3>Corrosion Resistance Guarantee</h3>
                      <p>Our materials are tested and certified for corrosion resistance, ensuring long-term structural integrity</p>
                    </div>
                  </div>
                  <div className="feature-detail-item">
                    <FaCheckCircle className="feature-check-icon" />
                    <div>
                      <h3>Low Maintenance</h3>
                      <p>Rust-proof materials mean minimal maintenance requirements, saving you time and money over the system's lifetime</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Specifications Table */}
        <section className="why-choose-specs-section">
          <div className="container">
            <h2 className="why-choose-specs-title">Technical Specifications</h2>
            <div className="specs-table-wrapper">
              <table className="specs-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Specification</th>
                    <th>Benefit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Wind Resistance</strong></td>
                    <td>Up to 150 km/h</td>
                    <td>Withstands severe storms and cyclones</td>
                  </tr>
                  <tr>
                    <td><strong>Material</strong></td>
                    <td>Hot-dip Galvanized Steel</td>
                    <td>Superior corrosion resistance</td>
                  </tr>
                  <tr>
                    <td><strong>Coating</strong></td>
                    <td>Premium Powder Coating</td>
                    <td>Additional protection layer</td>
                  </tr>
                  <tr>
                    <td><strong>Structural Design</strong></td>
                    <td>Pre-fabricated, Engineered</td>
                    <td>Maximum stability and safety</td>
                  </tr>
                  <tr>
                    <td><strong>Lifespan</strong></td>
                    <td>25+ Years</td>
                    <td>Long-term reliability</td>
                  </tr>
                  <tr>
                    <td><strong>Maintenance</strong></td>
                    <td>Minimal</td>
                    <td>Low ongoing costs</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="why-choose-comparison-section">
          <div className="container">
            <h2 className="why-choose-comparison-title">Our Structures vs. Standard Installations</h2>
            <div className="comparison-grid">
              <div className="comparison-card standard">
                <h3 className="comparison-card-title">Standard Installations</h3>
                <ul className="comparison-list">
                  <li className="comparison-item negative">
                    <span>Basic materials prone to rust</span>
                  </li>
                  <li className="comparison-item negative">
                    <span>Limited wind resistance</span>
                  </li>
                  <li className="comparison-item negative">
                    <span>Requires frequent maintenance</span>
                  </li>
                  <li className="comparison-item negative">
                    <span>Shorter lifespan (10-15 years)</span>
                  </li>
                  <li className="comparison-item negative">
                    <span>Vulnerable to weather damage</span>
                  </li>
                </ul>
              </div>
              <div className="comparison-card lunar">
                <div className="comparison-badge">Lunar Electric</div>
                <h3 className="comparison-card-title">Our Installations</h3>
                <ul className="comparison-list">
                  <li className="comparison-item positive">
                    <FaCheckCircle />
                    <span>Galvanized steel, rust-proof materials</span>
                  </li>
                  <li className="comparison-item positive">
                    <FaCheckCircle />
                    <span>Engineered for 150 km/h winds</span>
                  </li>
                  <li className="comparison-item positive">
                    <FaCheckCircle />
                    <span>Minimal maintenance required</span>
                  </li>
                  <li className="comparison-item positive">
                    <FaCheckCircle />
                    <span>25+ year lifespan guarantee</span>
                  </li>
                  <li className="comparison-item positive">
                    <FaCheckCircle />
                    <span>Storm-proof and weather-resistant</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Peace of Mind Section */}
        <section className="why-choose-peace-section">
          <div className="container">
            <div className="peace-content">
              <div className="peace-icon">
                <FaUmbrella size={64} style={{ color: '#1F3A93' }} />
              </div>
              <h2 className="peace-title">Peace of Mind for Decades</h2>
              <p className="peace-text">
                When you choose Lunar Electric, you're not just getting solar panels—you're investing in a system built to last. Our storm-proof and rust-proof structures ensure your solar investment is protected against weather damage and corrosion, giving you peace of mind for 25+ years.
              </p>
              <div className="peace-benefits">
                <div className="peace-benefit-item">
                  <FaHardHat className="peace-benefit-icon" />
                  <span>Professional Engineering</span>
                </div>
                <div className="peace-benefit-item">
                  <FaCertificate className="peace-benefit-icon" />
                  <span>Certified Materials</span>
                </div>
                <div className="peace-benefit-item">
                  <FaTools className="peace-benefit-icon" />
                  <span>Expert Installation</span>
                </div>
                <div className="peace-benefit-item">
                  <FaShieldAlt className="peace-benefit-icon" />
                  <span>Long-Term Protection</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="why-choose-cta-section">
          <div className="container">
            <div className="why-choose-cta-content">
              <h2 className="why-choose-cta-title">Ready to Invest in Durable Solar?</h2>
              <p className="why-choose-cta-text">
                Get a free consultation and discover how our storm-proof and rust-proof solar structures can protect your investment for decades to come.
              </p>
              <div className="why-choose-cta-buttons">
                <Link to="/callback" className="btn btn-primary btn-large">
                  Get Free Quote
                </Link>
                <a href="tel:+919148857417" className="btn btn-secondary btn-large">
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default WhyChooseUs;

